<template>
  <div
    ref="reviewsFeed"
    class="reviews-feed"
    :role="isError || (!items.length && !isInitialLoading) ? 'none' : 'feed'"
    :aria-busy="isInitialLoading"
  >
    <template v-if="isInitialLoading">
      <span class="sr-only" role="alert" aria-live="polite">
        {{ t('reviews-loading') }}
      </span>
      <div
        v-for="placeholder in placeholdersNumber"
        :key="placeholder"
        class="reviews-feed__item"
        aria-hidden="true"
      >
        <span
          class="reviews-feed__skeleton reviews-feed__skeleton--stars"
          aria-hidden="true"
        ></span>
        <span class="reviews-feed__skeleton reviews-feed__skeleton--date" aria-hidden="true"></span>
        <span class="reviews-feed__skeleton"></span>
        <span class="reviews-feed__skeleton"></span>
        <span class="reviews-feed__skeleton"></span>
      </div>
    </template>

    <template v-else-if="isError">
      <div class="reviews-feed__warning reviews-feed__warning--error">
        <i class="icon icon-Alert-info-triangle" aria-hidden="true"></i>
        <div class="reviews-feed__warning-heading">{{ t('reviews-error-heading') }}</div>
        <span>{{ t('reviews-error-description') }}</span>
        <button class="link" @click="onRefetch">{{ t('reviews-error-cta') }}</button>
      </div>
    </template>

    <template v-else-if="!items.length">
      <div class="reviews-feed__warning">
        <i class="icon icon-Face-neutral" aria-hidden="true"></i>
        <div class="reviews-feed__warning-heading">
          {{ t('reviews-no-items-heading') }}
        </div>
        <span>{{ t('reviews-no-items-description') }}</span>
      </div>
    </template>

    <template v-else>
      <span class="sr-only" role="alert" aria-live="polite">
        {{ t('reviews-loaded') }}
      </span>
      <article v-for="item of items" :key="item.id" class="reviews-feed__item">
        <div class="reviews-feed__rating">
          <div v-if="item.rating" class="reviews-feed__stars">
            <span
              v-for="(star, index) in getStarRating(item.rating)"
              :key="index"
              :class="['reviews-feed__star', star]"
            >
              <i class="icon icon-Star" aria-hidden="true"></i>
              <i v-if="star === 'half'" class="icon icon-Star" aria-hidden="true"></i>
            </span>
            <span class="sr-only">Rating: {{ item.rating }} stars</span>
          </div>
          <div v-if="item.reviewer?.name" class="reviews-feed__name">
            {{ item.reviewer.name }}
          </div>
        </div>
        <div v-if="item.date" class="reviews-feed__date">
          {{ dayjs(item.date).format('D MMMM YYYY') }}
        </div>
        <p v-if="item.comment" class="reviews-feed__text">
          {{ !item.isExpanded && item.trimmedComment ? item.trimmedComment : item.comment }}
        </p>
        <button
          v-if="item.trimmedComment || item.hasResponses"
          class="link reviews-feed__readmore"
          @click="toggleButtonFn(item, item.isExpanded)"
        >
          {{ item.isExpanded ? t('reviews-read-less') : t('reviews-read-more') }}
        </button>
        <div v-if="item.isExpanded" class="reviews-feed__responses">
          <template v-if="item.responsesLoading">
            <span class="sr-only" role="alert" aria-live="polite">
              {{ t('rating-response-loading') }}
            </span>
            <div class="reviews-feed__response" aria-hidden="true">
              <span
                class="reviews-feed__skeleton reviews-feed__skeleton--stars"
                aria-hidden="true"
              ></span>
              <span
                class="reviews-feed__skeleton reviews-feed__skeleton--date"
                aria-hidden="true"
              ></span>
              <span class="reviews-feed__skeleton" aria-hidden="true"></span>
              <span class="reviews-feed__skeleton" aria-hidden="true"></span>
            </div>
          </template>
          <template v-else-if="item.responses">
            <span class="sr-only" role="alert" aria-live="polite">
              {{ t('rating-response-loaded') }}
            </span>
            <div
              v-for="(response, index) in item.responses"
              :key="index"
              class="reviews-feed__response"
            >
              <div v-if="response.name" class="reviews-feed__response-name">
                {{ response.name }}
              </div>
              <div v-if="response.responseDate" class="reviews-feed__response-date">
                {{ dayjs(response.responseDate).format('D MMMM YYYY') }}
              </div>
              <p v-if="response.comment" class="reviews-feed__response-text">
                {{ response.comment }}
              </p>
            </div>
          </template>
        </div>
      </article>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import { getStarRating } from '../../../../utils/getStarRating';

const { t } = useI18n();

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  toggleButtonFn: {
    type: Function,
    default: () => null,
  },
  isInitialLoading: {
    type: Boolean,
    default: () => false,
  },
  isError: {
    type: Boolean,
    default: () => false,
  },
  placeholdersNumber: {
    type: Number,
    default: () => 5,
  },
});

const reviewsFeed = ref(null);
const lastFocusableItem = ref(null);

const emit = defineEmits(['focusable-item-changed', 'refetch']);

const onRefetch = () => emit('refetch');

const setLastFocusableItem = () => {
  if (!reviewsFeed.value) lastFocusableItem.value = null;
  else {
    const focusableItems = reviewsFeed.value.querySelectorAll('button');
    if (focusableItems.length) lastFocusableItem.value = focusableItems[focusableItems.length - 1];
    else lastFocusableItem.value = null;
  }

  emit('focusable-item-changed', lastFocusableItem.value);
};

watch(
  () => props.items,
  () => nextTick(() => setLastFocusableItem())
);
</script>

<style lang="scss">
@import './scss/ReviewsFeed.scss';
</style>
